import React, { ReactNode, useEffect, useState } from 'react';
import { useFetchApprovalDetails, useFetchApprovalHistory } from '../../hooks/approval-request-hooks';
import { HbrSpinner } from '@harbor/elements-react-standalone';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../support/store';
import {
  ApprovalRequest,
  ApprovalTemplateFieldSection,
} from '../../types/approval-request';
import StbSimpleTable from '../security-toolbox-ui/StbSimpleTable';
import { fetchItem } from '../../support/queries';
import { checkApprovalTemplatesLoaded } from '../../support/slices/approvalTemplatesSlice';
import StbAccordion from '../security-toolbox-ui/StbAccordion';
import StbLoader from '../security-toolbox-ui/StbLoader';
import { PHASE_COLOURS } from '../../support/config/tables';


const DLine = ({
  label,
  displayValue,
}: {
  label: string;
  displayValue?: string | ReactNode;
}) => {
  if (!displayValue) {
    return null;
  }
  return (
    <React.Fragment key={`approval-${label}-${displayValue}`}>
      <dd className='text-xs leading-3'>{label}</dd>
      <dt className='font-medium text-base capitalize pb-2'>{displayValue}</dt>
    </React.Fragment>
  );
};

const customPropFormat = (propId: string[], displayValue: ReactNode | string): ReactNode => {
  if (!propId) {
    return;
  }
  const stageNameFormat = () => {
    if (typeof displayValue !== 'string') {
      return displayValue;
    }

    const parts = (displayValue as string).split('-');
    const part0 = parts?.[0]?.trim();
    const part1 = parts?.[1]?.trim();
    if (!part0) {
      return displayValue;
    }
    return (
      <div className="flex items-center">
        <div
          className={`flex items-center justify-center w-5 h-5 rounded-full ${
            PHASE_COLOURS[part0]
          } mr-1 flex-0 text-md text-white -my-1`}
        >
          {part1 ? part0 : ''}
        </div>
        <div className='flex-1 whitespace-nowrap'>
          {part1 ? part1 : part0}
        </div>
      </div>
    )
  }
  switch (propId.at(-1)) {
    case 'StageName':
      return stageNameFormat();
    default:
      return displayValue;
  }
}

interface Props {
  approval: ApprovalRequest;
}

const ApprovalsListDetail = ({ approval }: Props) => {
  const [loadHistory, setLoadHistory] = useState(true);

  const approvalType = approval.requested_item_type;

  const dispatch = useAppDispatch();

  const { data: history, isLoading: isLoadingHistory } =
    useFetchApprovalHistory(approval.id, loadHistory);

  useEffect(() => {
    dispatch(checkApprovalTemplatesLoaded());
  }, [dispatch]);

  const template = useSelector(
    (state: RootState) =>
      state.approvalTemplates.templatesLookup[approvalType]
  );

  const objectIds = (template?.rootObjects ?? []).reduce(
    (a, c) => {
      a[c] = approval.requested_item[c].id ?? approval.requested_item[c];
      return ({...a});
    },
    {}
  );
  
  const { data: approvalData } = useFetchApprovalDetails(
    approvalType,
    Object.keys(template?.sections ?? {}),
    objectIds,
    { enabled: !!template && !!objectIds }
  );

  const getSingleValue = (objectName, templateField) => {
    let currentLocation = objectName;
    let fieldValue = <span className="italic font-normal">No value</span>;
    templateField.id.forEach((idFragment: string) => {
      currentLocation = currentLocation[idFragment];
      if (!!currentLocation && ['string', 'number'].includes(typeof currentLocation)) {
        fieldValue = currentLocation;
      }
    });
    return fieldValue;
  }

  const getValue = (objectName, field): ReactNode => {
    let fieldValue: ReactNode = <span className="italic font-normal">No value</span>;
    if(field.type === 'field') {
      fieldValue = getSingleValue(approvalData[objectName], field);
    } else if (field.type === 'group') {
      fieldValue = 
        <dl className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 my-1">
          {field.fields.map(field => (
            <React.Fragment key={`${objectName}-${field.id}`}>
              <dd>{field.label}: </dd><dt>{getSingleValue(approvalData[objectName], field)}</dt>
            </React.Fragment>
          ))}
        </dl>;
    } else if (field.type === 'collection') {
      fieldValue = <ul className="">
        {!approvalData[objectName]?.length && (<li className="italic font-normal">No value</li>)}
        {(approvalData[objectName] ?? []).map((item, n) => (
          <li key={n}>{item[field.fields[0].id] ?? <span className="italic font-normal">No value</span>}</li>
        ))}
      </ul>
    }
    return customPropFormat(field.id ?? 'default', fieldValue);
  }

  if (!template || !approvalData) return (<StbLoader isLoading={!template} />);

  return (
    <div className='w-full max-w-[calc(100vw_-_320px)] pt-4 grid grid-cols-1 xl:grid-cols-2'>
      <div className="satssatsat">
        {(Object.keys(template.sections) ?? []).map((key: string) => (
          <dl key={`approval-detail-${approval.id}-${key}`}>
            {template.sections[key].map((field: ApprovalTemplateFieldSection) => (
              <DLine key={`approval-detail-${key}-${field.id}`} label={field.label} displayValue={getValue(key, field)} />
            ))}
          </dl>
        ))}
      </div>
      {/* <div onClick={() => setLoadHistory(true)}> */}
      <div>
        <StbAccordion
          variant='minimal'
          headerContent={<h3>History</h3>}
          isOpen={true}
        >
          <div className='px-4 max-w-3xl'>
            {isLoadingHistory && <HbrSpinner size='small' />}
            {!isLoadingHistory && !!history?.length && (
              <StbSimpleTable
                columns={['Date', 'Status', 'User', 'Justification']}
                data={history.reduce((a, c, n) => {
                  a[n.toString()] = [
                    new Date(c?.created).toLocaleDateString('en-US'),
                    c.current_status?.title ?? 'Unknown status',
                    c.action_by_user?.email ?? 'No action',
                    c?.current_justification ?? null,
                  ];
                  return a;
                }, {})}
                loading={false}
              />
            )}
            {!isLoadingHistory && !history?.length && <p className="mb-4">None</p>}
          </div>
        </StbAccordion>
      </div>
    </div>
  );
};

export default ApprovalsListDetail;
